import { FormEvent, useEffect, useState } from 'react';
import { SlideOver } from '@/components/SlideOver.js';
import type { CustomerDetail, PreferredContact, UpdateFrequency } from '@/lib/api/customers.js';

export type CommPreferencesValues = {
  preferredContact: PreferredContact;
  quietHoursStart: string;
  quietHoursEnd: string;
  quietHoursEnabled: boolean;
  languagePreference: string;
  marketingOptIn: boolean;
  surveyOptIn: boolean;
  reviewRequestOptIn: boolean;
  updateFrequency: UpdateFrequency;
  smsOptIn: boolean;
};

function valuesFromCustomer(customer: CustomerDetail): CommPreferencesValues {
  return {
    preferredContact: (customer.preferredContact as PreferredContact) ?? 'sms',
    quietHoursStart: (customer.quietHoursStart ?? '20:00').slice(0, 5),
    quietHoursEnd: (customer.quietHoursEnd ?? '09:00').slice(0, 5),
    quietHoursEnabled: customer.quietHoursEnabled ?? false,
    languagePreference: customer.languagePreference ?? 'en',
    marketingOptIn: customer.marketingOptIn ?? false,
    surveyOptIn: customer.surveyOptIn ?? true,
    reviewRequestOptIn: customer.reviewRequestOptIn ?? true,
    updateFrequency: (customer.updateFrequency as UpdateFrequency) ?? 'status_change',
    smsOptIn: customer.smsOptIn ?? true,
  };
}

const CONTACT_LABEL: Record<PreferredContact, string> = {
  sms: 'SMS',
  email: 'Email',
  phone: 'Phone call only',
  none: 'No automated messages',
};

const FREQ_LABEL: Record<UpdateFrequency, string> = {
  every_update: 'Every status change',
  status_change: 'Only on key updates (intake + ready)',
  ready_only: 'Only when ready for pickup',
};

export function CommPreferencesCard({
  customer,
  onEdit,
}: {
  customer: CustomerDetail;
  onEdit: () => void;
}) {
  const v = valuesFromCustomer(customer);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-950">Communication Preferences</h3>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Edit
        </button>
      </div>
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium text-slate-500">Preferred contact</dt>
          <dd className="font-semibold text-slate-950">{CONTACT_LABEL[v.preferredContact]}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Updates</dt>
          <dd className="font-semibold text-slate-950">{FREQ_LABEL[v.updateFrequency]}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Quiet hours</dt>
          <dd className="font-semibold text-slate-950">
            {v.quietHoursEnabled ? `${v.quietHoursStart} – ${v.quietHoursEnd}` : 'Off'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Language</dt>
          <dd className="font-semibold text-slate-950">
            {v.languagePreference === 'es' ? 'Spanish' : v.languagePreference === 'fr' ? 'French' : 'English'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Marketing messages</dt>
          <dd className="font-semibold text-slate-950">{v.marketingOptIn ? 'Opted in' : 'Opted out'}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Repair surveys</dt>
          <dd className="font-semibold text-slate-950">{v.surveyOptIn ? 'Opted in' : 'Opted out'}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">Review requests</dt>
          <dd className="font-semibold text-slate-950">{v.reviewRequestOptIn ? 'Opted in' : 'Opted out'}</dd>
        </div>
      </dl>
    </section>
  );
}

export function CommPreferencesSlideOver({
  open,
  onClose,
  customer,
  loading,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  customer: CustomerDetail;
  loading?: boolean;
  onSubmit: (values: CommPreferencesValues) => void;
}) {
  const [form, setForm] = useState<CommPreferencesValues>(() => valuesFromCustomer(customer));

  useEffect(() => {
    if (open) setForm(valuesFromCustomer(customer));
  }, [open, customer]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit(form);
  }

  return (
    <SlideOver title="Communication Preferences" open={open} onClose={onClose} panelClassName="max-w-lg">
      <form className="space-y-5" onSubmit={handleSubmit}>
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Contact method</legend>
          <p className="text-xs text-slate-500">How does this customer prefer to be contacted?</p>
          {(
            [
              ['sms', 'SMS (recommended)'],
              ['email', 'Email'],
              ['phone', 'Phone call only'],
              ['none', 'No automated messages'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name="preferredContact"
                checked={form.preferredContact === value}
                onChange={() => setForm((f) => ({ ...f, preferredContact: value }))}
              />
              {label}
            </label>
          ))}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-slate-900">Update frequency</legend>
          <p className="text-xs text-slate-500">When to send repair updates:</p>
          {(
            [
              ['every_update', 'Every status change'],
              ['status_change', 'Only on key updates (intake + ready)'],
              ['ready_only', 'Only when ready for pickup'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name="updateFrequency"
                checked={form.updateFrequency === value}
                onChange={() => setForm((f) => ({ ...f, updateFrequency: value }))}
              />
              {label}
            </label>
          ))}
        </fieldset>

        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-900">Quiet hours</div>
          <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium">
            <span>Enable quiet hours</span>
            <input
              type="checkbox"
              checked={form.quietHoursEnabled}
              onChange={(e) => setForm((f) => ({ ...f, quietHoursEnabled: e.target.checked }))}
            />
          </label>
          {form.quietHoursEnabled ? (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Don&apos;t send between</span>
                <input
                  type="time"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.quietHoursStart}
                  onChange={(e) => setForm((f) => ({ ...f, quietHoursStart: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">and</span>
                <input
                  type="time"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={form.quietHoursEnd}
                  onChange={(e) => setForm((f) => ({ ...f, quietHoursEnd: e.target.value }))}
                />
              </label>
            </div>
          ) : null}
        </div>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Language</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={form.languagePreference}
            onChange={(e) => setForm((f) => ({ ...f, languagePreference: e.target.value }))}
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
          </select>
        </label>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-slate-900">Opt-ins</div>
          {(
            [
              ['smsOptIn', 'SMS messages'],
              ['marketingOptIn', 'Marketing messages (promotions, deals)'],
              ['surveyOptIn', 'Satisfaction surveys (NPS)'],
              ['reviewRequestOptIn', 'Review requests (Google reviews)'],
            ] as const
          ).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium"
            >
              <span>{label}</span>
              <input
                type="checkbox"
                checked={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
              />
            </label>
          ))}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-white disabled:opacity-50"
        >
          {loading ? 'Saving…' : 'Save Preferences'}
        </button>
      </form>
    </SlideOver>
  );
}
